import { PagesizeType } from '../../service/pagination/pagesize-type';
import { Component, Input, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { OnyxEvent } from '../../models/event';
import { Observable } from 'rxjs/Observable';
import { List } from 'immutable';
import { ReservationCompare } from '../../models/reservation-compare';
import { ReservationCompareStore } from '../../service/reservation-compare-store';
import { NumberUtils } from '../../../shared/utils/number.util';
import { ReservationStatusCode } from '../../models/reservation-status-code.enum';
import { ReservationUtils } from '../../utils/reservation-utils';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationModalComponent } from '../../../shared/confirmation-modal/confirmation-modal.component';
import { ReservationStore } from '../../service/reservation-store';
import { Reservation } from '../../models/reservation';
import { EventReservationSource } from '../../models/event-reservation-source';
import { Constants } from '../../../shared/constants';
import { ReservationModalComponent } from '../reservation-modal/reservation-modal.component';
import { EventReservationAction } from '../../models/event-reservation-action';
import { SummaryRecordTypeEnum } from '../../models/summary-record-type.enum';
import { Page } from 'app/shared/models/page';
import { Subscription } from 'rxjs/Subscription';
import { UserLocalStorageService } from '../../../permission/services/user.local.storage.service';

@Component({
  selector: 'pioneer-reservation-summary-table',
  templateUrl: './reservation-summary-table.component.html',
  styleUrls: ['./reservation-summary-table.css']
})
export class ReservationSummaryTableComponent implements OnInit, OnDestroy {

  public onyxEvent: OnyxEvent;

  public editModal: EventReservationAction;

  public reservations: Observable<List<ReservationCompare>>;

  public statusCodes: Observable<ReservationStatusCode[]>;

  public filteredClass = '';

  public reservationToDelete: Reservation;

  public reservationToUnlink: Reservation;

  public headerRecordType: SummaryRecordTypeEnum;

  @ViewChild('confirmDeleteModal')
  public confirmDeleteModal: ConfirmationModalComponent;

  @ViewChild('confirmUnlinkModal')
  public confirmUnlinkModal: ConfirmationModalComponent;

  @ViewChild(ReservationModalComponent)
  public reservationModal: ReservationModalComponent;

  public reservationsPage: Page<ReservationCompare>;

  pagesizeType = PagesizeType.GROUP_RESERVATIONS_SUMMARY;

  private reservationsSubscription: Subscription = new Subscription();

  constructor(private reservationCompareStore: ReservationCompareStore,
              private translateService: TranslateService,
              private reservationStore: ReservationStore,
              private userLocalStorageService: UserLocalStorageService) {
  }

  @Input('event')
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  ngOnInit() {
    this.statusCodes = this.reservationCompareStore.getActiveStatusCodes();
    this.reservations = this.reservationCompareStore.getReservationList()
      .map(reservationList => ReservationUtils.onlyReservationsFilter(reservationList));
    this.editModal = EventReservationAction.EDIT_RESERVATION;

    this.isFiltered().subscribe(isFiltered => {
      if (isFiltered) {
        this.filteredClass = 'filtered';
      } else {
        this.filteredClass = '';
      }
    });

    this.headerRecordType = SummaryRecordTypeEnum.RESERVATION;

    this.reservationsPage = Page.empty<ReservationCompare>();
    this.reservationsPage.size = this.userLocalStorageService.getPagesize(PagesizeType.GROUP_RESERVATIONS_SUMMARY);

    this.reservationsSubscription.add(this.getFilteredReservations().subscribe(reservationList => {
      const page = this.reservationsPage.number;
      const itemsPerPage = this.reservationsPage.size;

      this.reservationsPage.content = List<ReservationCompare>(
        reservationList.slice(page * itemsPerPage, (page + 1) * itemsPerPage)
      );
      this.reservationsPage.totalElements = reservationList.size;
      this.reservationsPage.numberOfElements = this.reservationsPage.content.size;
    }));

  }

  ngOnDestroy(): void {
    this.reservationsSubscription.unsubscribe();
  }

  getReservations(): Observable<List<ReservationCompare>> {
    return this.reservations;
  }

  getRsvps(): Observable<List<ReservationCompare>> {
    return this.reservationCompareStore.getReservationList()
      .map(reservationList => ReservationUtils.onlyRsvpFilter(reservationList));
  }

  getFilteredReservations(): Observable<List<ReservationCompare>> {
    return this.reservationCompareStore.getCurrentFilteredReservations()
      .map(reservationList => ReservationUtils.onlyReservationsFilter(reservationList));
  }

  getReservationRevenueTotal(): Observable<number> {
    return ReservationUtils.calculateRevenueTotal(this.getReservations());
  }

  getFilteredReservationRevenueTotal(): Observable<number> {
    return ReservationUtils.calculateRevenueTotal(this.getFilteredReservations());
  }

  getReservationCommissionTotal(): Observable<number> {
    return ReservationUtils.calculateCommissionTotal(this.getReservations());
  }

  getRsvpCommissionTotal(): Observable<number> {
    return ReservationUtils.calculateRsvpCommissionTotal(this.getRsvps());
  }

  getFilteredReservationCommissionTotal(): Observable<number> {
    return ReservationUtils.calculateCommissionTotal(this.getFilteredReservations());
  }

  getFilteredReservationNightsTotal(): Observable<number> {
    return ReservationUtils.calculateReservationTotalNights(this.getFilteredReservations());
  }

  isReservationListEmpty(): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.getReservations());
  }

  isRsvpListEmpty(): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.getRsvps());
  }

  isFilteredListEmpty(): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.getFilteredReservations());
  }

  isFiltered(): Observable<boolean> {
    const filteredSizeObs = ReservationUtils.getListSize(this.getFilteredReservations());
    const unfilteredSizeObs = ReservationUtils.getListSize(this.getReservations());

    return filteredSizeObs.flatMap(filteredSize => {
      return unfilteredSizeObs.map(unfilteredSize => {
        return filteredSize !== unfilteredSize;
      });
    });
  }

  getCommissionDelta(): Observable<number> {
    const reservationCommissionObj = this.getReservationCommissionTotal();
    const rsvpCommissionObj = this.getRsvpCommissionTotal();

    return reservationCommissionObj.flatMap(reservationCommission => {
      return rsvpCommissionObj.map(rsvpCommission => {
        return reservationCommission - rsvpCommission;
      });
    }).map(total => NumberUtils.round2Fixed(total));
  }

  getCommissionDeltaText(): Observable<string> {
    return this.getCommissionDelta().flatMap(delta => {
      if (delta > 0) {
        return this.translateService.get('EVENT.RESERVATION.TOOLTIP_COMMISSION_DIFFERENCE_MORE');
      } else if (delta < 0) {
        return this.translateService.get('EVENT.RESERVATION.TOOLTIP_COMMISSION_DIFFERENCE_LESS');
      } else {
        return this.translateService.get('EVENT.RESERVATION.TOOLTIP_COMMISSION_DIFFERENCE_EQUAL');
      }
    });
  }

  hasFilteredOutResults(): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.getFilteredReservations()).flatMap(filteredListIsEmpty => {
      return this.isReservationListEmpty().map(isReservationListEmpty => filteredListIsEmpty !== isReservationListEmpty);
    });
  }

  resetStatusCodes(): void {
    this.reservationCompareStore.resetStatusCodes();
  }

  totalCommissionClass(): Observable<string> {
    return this.reservationCompareStore.areRsvpsPresent().flatMap(hasRsvps => {
      return this.getCommissionDelta().map( total => {
        return hasRsvps && total > 0 ? 'commission-up' : hasRsvps && total < 0 ? 'commission-down' : '';
      } );
    });
  }

  getStatusClass(compare: ReservationCompare): string {
    if (compare.reservation) {
      switch (compare.reservation.status) {
        case 'R':
          return 'res-status-reserved';
        case 'I':
          return 'res-status-checked-in';
        case 'O':
          return 'res-status-checked-out';
        case 'C':
          return 'res-status-cancelled';
        case 'N':
          return 'res-status-no-show';
        default:
          return '';
      }
    }
    return '';
  }

  getSourceClass(compare: ReservationCompare): string {
    if (compare.reservation) {
      switch (compare.reservation.source) {
        case EventReservationSource.FILE:
          return 'btn-lock';
        case EventReservationSource.MANUAL:
          return 'btn-more';
        case EventReservationSource.UPLOADED:
          return 'btn-more';
        case EventReservationSource.RSVP:
          return 'btn-more';
        case EventReservationSource.OUT_OF_BLOCK:
          return 'btn-lock';
        default:
          return 'btn-lock';
      }
    }
    return '';
  }

  isCommissionUp(compare: ReservationCompare): boolean {
    return compare.rsvp && compare.delta && compare.delta.commission > 0;
  }

  isCommissionDown(compare: ReservationCompare): boolean {
    return compare.rsvp && compare.delta && compare.delta.commission < 0;
  }

  confirmBeforeDelete(event: any, reservation: Reservation) {
    event.preventDefault();
    this.reservationToDelete = reservation;
    this.confirmDeleteModal.open();
  }

  confirmHandler(deleteConfirmed: boolean) {
    if (deleteConfirmed) {
      this.deleteReservation(this.reservationToDelete);
    }
    this.reservationToDelete = null;
  }

  deleteReservation(reservation: Reservation): Observable<boolean> {
    const obs = this.reservationStore.deleteReservation(reservation);
    obs.subscribe(() => {
      this.reservationCompareStore.refreshCurrentReservationList(this.onyxEvent.eventId);
    });
    return obs;
  }

  canBeModifiable(reservation: Reservation): boolean {
    return (Constants.UI_MODIFIABLE_SOURCES.indexOf(reservation.source) >= 0);
  }

  canBeUnlinkable(reservation: Reservation): boolean {
    return (Constants.UI_UNLINKABLE_SOURCES.indexOf(reservation.source) >= 0);
  }

  openReservationModal(event: any, reservation: Reservation) {
    event.preventDefault();
    this.reservationModal.open(reservation);
  }

  get canManageReservations(): boolean {
    return this.onyxEvent.canManageReservations;
  }

  confirmBeforeUnlink(event: any, reservation: Reservation) {
    event.preventDefault();
    this.reservationToUnlink = reservation;
    this.confirmUnlinkModal.open();
  }

  confirmUnlinkHandler(unlinkConfirmed: boolean) {
    if (unlinkConfirmed) {
      this.unlinkReservation(this.reservationToUnlink);
    }
    this.reservationToUnlink = null;
  }

  unlinkReservation(reservation: Reservation): Observable<boolean> {
    const obs = this.reservationStore.unlinkReservation(reservation.eventReservationId, this.onyxEvent.eventId);
    obs.subscribe(() => {
      this.reservationCompareStore.refreshCurrentReservationList(this.onyxEvent.eventId);
    });
    return obs;
  }

  onRefreshReservationsList(pageNumber: number, itemsPerPage: number) {
    this.getFilteredReservations().take(1).subscribe(reservationList => {
      this.reservationsPage.content = List<ReservationCompare>(
        reservationList.slice(pageNumber * itemsPerPage, (pageNumber + 1) * itemsPerPage)
      );
      this.reservationsPage.number = pageNumber;
      this.reservationsPage.size = itemsPerPage;
      this.reservationsPage.totalElements = reservationList.size;
      this.reservationsPage.numberOfElements = this.reservationsPage.content.size;
    });
  }

  isTransient(reservation: Reservation) {
    return reservation.source === EventReservationSource.OUT_OF_BLOCK;
  }

  getTransientTooltip(reservation: Reservation): String {
    return reservation.payViaGp ? 'EVENT.RESERVATION.TOOLTIP_RESERVATION_PAY_VIA_GP'
      : 'EVENT.RESERVATION.TOOLTIP_RESERVATION_NOT_PAY_VIA_GP';
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/reservations/reservation-summary-table/reservation-summary-table.component.ts